import { Trans, useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { useAccount } from 'wagmi'
import { getLonBalance } from '~/apis'
import { useModalOpened, useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { formatNumber } from '~/utils/numbers'
import Modal from '../Modal'
import { RowBetween } from '../Layout'
import { Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  Container,
  StyledTitle,
  StyledLONBalance,
  StyledNote,
  StyledButton,
  StyledTableHeader,
} from './styled'
import { useFeeFactor, useTotalEqualLon } from '~/state/user/hooks'
import { toBigNumber } from '~/utils/numbers'
import Tooltip from '~/components/Tooltip'
import IconQuestionMark from '~/assets/images/question-mark-gray.svg'
import { useUpdaterChainId } from '~/hooks'
import { isMainnetOrSepolia } from '~/utils/chain'

interface DataType {
  key: string
  balance: string
  volume: string
  fee: string
}

export const FeeIntroModal = () => {
  const { t, i18n } = useTranslation()
  const { address: account } = useAccount()
  const chainId = useUpdaterChainId()
  const isSupportedChain = isMainnetOrSepolia(chainId)
  const { data: lonBalance } = useSWR(
    isSupportedChain ? ['getLonBalance', chainId, account] : null,
    () => getLonBalance(chainId, account),
  )
  const totalEqualLon = useTotalEqualLon()
  const totalLonBalance = toBigNumber(lonBalance).plus(totalEqualLon).toString()

  const toggle = useModalToggle(ApplicationModal.FEE_INTRO)
  const isOpen = useModalOpened(ApplicationModal.FEE_INTRO)

  const lang = i18n.resolvedLanguage === 'zh' ? 'zh-cn' : 'en-us'

  const columns: ColumnsType<DataType> = [
    {
      title: <StyledTableHeader>{t('lon_holding_balance')}</StyledTableHeader>,
      dataIndex: 'balance',
    },
    {
      title: <StyledTableHeader>{t('in_30d_volume')}</StyledTableHeader>,
      dataIndex: 'volume',
    },
    {
      title: <StyledTableHeader>{t('trade_detail_fee')}</StyledTableHeader>,
      dataIndex: 'fee',
    },
  ]

  const data: DataType[] = [
    {
      key: '1',
      balance: '<1',
      volume: '<30k USD',
      fee: '0.30%',
    },
    {
      key: '2',
      balance: '≥1',
      volume: '≥30k USD',
      fee: '0.29%',
    },
    {
      key: '3',
      balance: '≥5',
      volume: '≥50k USD',
      fee: '0.28%',
    },
    {
      key: '4',
      balance: '≥20',
      volume: '≥100k USD',
      fee: '0.26%',
    },
    {
      key: '5',
      balance: '≥100',
      volume: '≥200k USD',
      fee: '0.24%',
    },
    {
      key: '6',
      balance: '≥500',
      volume: '≥500k USD',
      fee: '0.22%',
    },
    {
      key: '7',
      balance: '≥1,000',
      volume: '≥1m USD',
      fee: '0.20%',
    },
    {
      key: '8',
      balance: '≥2,000',
      volume: '≥2m USD',
      fee: '0.18%',
    },
    {
      key: '9',
      balance: '≥5,000',
      volume: '≥5m USD',
      fee: '0.15%',
    },
    {
      key: '10',
      balance: '≥10,000',
      volume: '≥10m USD',
      fee: '0.10%',
    },
  ]

  const lonBalanceLiteral = totalLonBalance
    ? `${formatNumber(totalLonBalance, 6)} LON`
    : '~'

  const isZeroBalance = totalLonBalance === '0'

  // 获取交易费费率
  const feeFactor = useFeeFactor()
  const isDiscountedFee = feeFactor !== 30
  const formatFee = feeFactor ? `${(feeFactor / 100).toFixed(2)}%` : '~'
  const feeLiteral = feeFactor
    ? isDiscountedFee
      ? ` ${formatFee} (${t('discounted_trade_fee')})`
      : ` (${t('standard')})`
    : '~'

  const userLevel = data.find((item) => item.fee === formatFee)

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={toggle}
      animate={false}
      contentClassName="fee-intro"
    >
      <Container>
        <StyledTitle>{t('trade_fee_intro_title')}</StyledTitle>
        <StyledLONBalance>
          <RowBetween>
            <p className="main-label">{t('current_trade_fee')}</p>
            <p className="main-desc">
              <span className={isDiscountedFee ? 'through' : ''}>0.30%</span>
              <span className={isDiscountedFee ? 'highlight' : ''}>
                {feeLiteral}
              </span>
            </p>
          </RowBetween>
          {isDiscountedFee && isZeroBalance ? (
            <RowBetween>
              <p className="label">{t('last_30d_volume')}</p>
              <p className="desc">{userLevel?.volume}</p>
            </RowBetween>
          ) : (
            <RowBetween>
              <div className="label-wrapper">
                <p className="label">{t('lon_holding_balance')}</p>
                <Tooltip
                  className="fee-intro-tooltip"
                  placement="top"
                  title={t('lon_holding_balance_tip')}
                  trigger="hover"
                  img={IconQuestionMark}
                />
              </div>
              <p className="desc">{lonBalanceLiteral}</p>
            </RowBetween>
          )}
        </StyledLONBalance>
        <Table
          columns={columns}
          dataSource={data}
          bordered
          pagination={false}
          className="table"
          rowClassName={(record) =>
            record.fee === formatFee ? 'highlight' : ''
          }
        />
        <StyledNote>
          <Trans i18nKey="trade_fee_intro_note">
            注：以上条件满足其中之一即可享受费率优惠，更多关于
            <a
              href={`https://support.tokenlon.im/hc/${lang}/articles/360037260272`}
              target="_blank"
            >
              Tokenlon 费率介绍
            </a>
            。
          </Trans>
        </StyledNote>
        <StyledButton onClick={toggle}>{t('ok')}</StyledButton>
      </Container>
    </Modal>
  )
}
